import {
  FC,
  useState,
  createContext,
  ReactNode,
  useContext,
  useCallback,
  useMemo,
} from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { lightTheme, darkTheme } from 'theme/theme';
import { Theme, ThemeProvider as MuiThemeProvider } from '@mui/material';
import createSxStyles, {
  CreateSxStyles,
} from 'helpers/styleHelpers/createSxStyles';
import mergeSxStyles, {
  MergeSxStyles,
} from 'helpers/styleHelpers/mergeSxStyles';
import createCssStyles, {
  CreateCssStyles,
} from 'helpers/styleHelpers/createCssStyles';
import themeConstants from 'theme/themeConstants';

interface ThemeContextProps {
  themeMode: 'light' | 'dark';
  theme: Theme;
  themeConstants: typeof themeConstants;
  createSxStyles: CreateSxStyles;
  mergeSxStyles: MergeSxStyles;
  createCssStyles: CreateCssStyles;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextProps>({
  themeMode: 'light',
  theme: lightTheme,
  themeConstants,
  createSxStyles: () => {
    throw new Error(
      "createSxStyles cannot be called outside of the ThemeProvider's context.",
    );
  },
  mergeSxStyles: () => {
    throw new Error(
      "mergeSxStyles cannot be called outside of the ThemeProvider's context.",
    );
  },
  createCssStyles: () => {
    throw new Error(
      "createCssStyles cannot be called outside of the ThemeProvider's context.",
    );
  },
  toggleTheme: () => {
    throw new Error(
      "toggleTheme cannot be called outside of the ThemeProvider's context.",
    );
  },
});

interface ThemeProviderProps {
  children: ReactNode;
}

const ThemeProvider: FC<ThemeProviderProps> = ({ children }) => {
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const defaultTheme = useMemo<ThemeContextProps['themeMode']>(
    () => (prefersDarkMode ? 'dark' : 'light'),
    [prefersDarkMode],
  );

  const [themeMode, setThemeMode] =
    useState<ThemeContextProps['themeMode']>(defaultTheme);

  const toggleTheme = useCallback(() => {
    setThemeMode((prevValue) => (prevValue === 'light' ? 'dark' : 'light'));
  }, []);

  const providerValue = useMemo(
    () => ({
      themeMode,
      theme: themeMode === 'light' ? lightTheme : darkTheme,
      themeConstants,
      createSxStyles,
      mergeSxStyles,
      createCssStyles,
      toggleTheme,
    }),
    [themeMode, toggleTheme],
  );

  return (
    <ThemeContext.Provider value={providerValue}>
      <MuiThemeProvider theme={providerValue.theme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;

export const useThemeContext = () => useContext(ThemeContext);
